import { useState, useEffect, useMemo, memo, useCallback } from 'react';
import { useSelector, useDispatch } from '../imports/generalImport';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import {
  Container,
  Skeleton,
  Box,
  useMediaQuery,
  Typography,
} from '../imports/muiImport';
import { fetchDashboardData } from '../store/dashboard/action.js';
import { alpha, useTheme } from '@mui/system';

export const SliderComponent = ({
  height = 420,
  showPagination = true,
}) => {
  const slider = useSelector((state) => state.Dashboard?.slider);
  const loading = useSelector((state) => state.Dashboard?.isLoading);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:600px)');
  const theme = useTheme();

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'center',
      skipSnaps: false,
      duration: 30,
    },
    [
      Autoplay({
        delay: 3500,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
      }),
    ]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  useEffect(() => {
    if (!slider && !loading) {
      dispatch(fetchDashboardData());
    }
  }, [dispatch, slider, loading]);

  // Filter sliders based on device type using API images
  const filteredSliders = useMemo(() => {
    if (!slider || !Array.isArray(slider)) return [];

    return slider.filter((item) =>
      item && (isMobile ? item?.mobileImage : item?.webImage)
    );
  }, [isMobile, slider]);

  const scrollTo = useCallback(
    (index) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  );

  const onInit = useCallback((emblaApi) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);

    emblaApi.on('reInit', onInit);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('reInit', onInit);
      emblaApi.off('reInit', onSelect);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onInit, onSelect]);

  // Error state
  if (slider?.error) {
    return (
      <Container
        disableGutters
        maxWidth={false}
        sx={{
          height: height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'error.main',
        }}
      >
        <Typography>{slider.error}</Typography>
      </Container>
    );
  }

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        position: 'relative',
        userSelect: 'none',
        touchAction: 'pan-y',
        overflow: 'hidden',
        borderRadius: 4,
        py: {
          xs: 0,
          lg: 1
        },
         mt:{
          xs: 1,
          md:6
        }

      }}
    >
      {loading ? (
        <Skeleton
          variant="rectangular"
          animation="wave"
          width="100%"
          sx={{
            height: isMobile ? 170 : 350,
            borderRadius: 2,
          }}
        />
      ) : (
        <Box sx={{
          position: 'relative',
          borderRadius: 4,
          "&:hover": {
            boxShadow: {
              xs: 6,
              lg: 3,
            }
          }
        }}>
          <Box
            ref={emblaRef}
            sx={{
              overflow: 'hidden',
              width: '100%',
              fontWeight: 600,

            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1
              }}
            >
              {filteredSliders.map((item, index) => {
                const key = `hero-slider-${item?.id || index}`;

                return (
                  <Box
                    key={key}
                    sx={{
                      flex: '0 0 100%',
                      minWidth: "100%",
                      height: isMobile ? 'auto' : { md: 'auto', lg: 'auto' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      position: 'relative',
                      cursor: item?.url ? 'pointer' : 'default',
                    }}
                    onClick={() => {
                      if (item?.url) {
                        window.location.href = item.url;
                      }
                    }}
                  >
                    <SliderImage
                      item={item}
                      isMobile={isMobile}
                      height={height}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}

      {showPagination && scrollSnaps.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 1,
          }}
        >
          {scrollSnaps.map((_, index) => (
            <Box
              key={index}
              onClick={() => scrollTo(index)}
              sx={{
                width: selectedIndex === index ? 24 : 8,
                height: 8,
                mx: 0.5,
                borderRadius: {
                  xs: 6,
                  lg: 4
                },
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backgroundColor: selectedIndex === index
                  ? alpha(theme.palette.secondary.main, 0.8)
                  : alpha(theme.palette.secondary.main, 0.2),
              }}
            />
          ))}
        </Box>
      )}
    </Container>
  );
};


const SliderImage = memo(({ item, isMobile, height }) => {

  const imageSrc = useMemo(() => {
    return isMobile ? item?.mobileImage : item?.webImage;
  }, [isMobile, item?.mobileImage, item?.webImage]);

  if (!imageSrc) {
    return (
      <Box
        sx={{
          height: height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',

        }}
      >
        <Typography>No Image</Typography>
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={imageSrc}
      alt={item?.title || 'slider image'}
      loading="lazy"
      sx={{
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        maxHeight: height,
        objectFit: { xs: 'contain', sm: "cover" },
        objectPosition: 'center',
        display: 'block',
        userSelect: 'none',

        borderRadius: {
          xs: 2,
          sm: 2,
        },

      }}
    />
  );
});

SliderImage.displayName = 'SliderImage';
